import { api } from "@/api";
import type {
  BlogTopic,
  TopicConflict,
  TopicEvaluation,
  TopicVerdict,
} from "@/shared/blog/topics";
import type { ListTopicsParams, TopicDraft, TopicsService } from "./topicsService.contract";

/**
 * Implementação HTTP do banco de pautas. O notifyme ainda não expõe estes
 * endpoints (E4.4) — este arquivo documenta o contrato esperado (ver
 * `docs/blog/api-contracts.md`) e passa a valer assim que o backend existir,
 * sem o front precisar mudar nada além do env `VITE_BLOG_SERVICE`.
 */

const BACKOFFICE = "v1/backoffice/blog/topics";

interface ApiTopic {
  id: string;
  title: string;
  cluster_id: string | null;
  primary_keyword: string;
  search_intent: BlogTopic["searchIntent"];
  funnel: BlogTopic["funnel"];
  format: BlogTopic["format"];
  angle: string;
  priority: number;
  status: BlogTopic["status"];
  source_urls: string[];
  post_id: string | null;
  discard_reason: string | null;
  evaluation_score?: number | null;
  evaluation_verdict?: TopicVerdict | null;
  evaluation?: ApiTopicEvaluation | null;
  monthly_searches?: number | null;
  conflict_post_id?: string | null;
  evaluated_at?: string | null;
  created_at: string;
  updated_at: string;
}

interface ApiTopicConflict {
  kind: TopicConflict["kind"];
  post_id: string | null;
  topic_id: string | null;
  title: string;
  matched: string;
  impressions: number | null;
  position: number | null;
}

interface ApiTopicEvaluation {
  demand: number | null;
  fit: number | null;
  cluster_gap: number | null;
  ease: number | null;
  conflicts: ApiTopicConflict[] | null;
  rationale: string;
  suggestion: string | null;
  model: string | null;
}

const mapEvaluation = (apiEvaluation: ApiTopicEvaluation): TopicEvaluation => ({
  demand: apiEvaluation.demand,
  fit: apiEvaluation.fit,
  clusterGap: apiEvaluation.cluster_gap,
  ease: apiEvaluation.ease,
  conflicts: (apiEvaluation.conflicts ?? []).map(c => ({
    kind: c.kind,
    postId: c.post_id,
    topicId: c.topic_id,
    title: c.title,
    matched: c.matched,
    impressions: c.impressions,
    position: c.position,
  })),
  rationale: apiEvaluation.rationale,
  suggestion: apiEvaluation.suggestion,
  model: apiEvaluation.model,
});

// Os campos de avaliação chegam opcionais: o notifyme anterior ao
// `topic-evaluation` não os envia, e a tela precisa seguir funcionando.
const mapTopic = (apiTopic: ApiTopic): BlogTopic => ({
  id: apiTopic.id,
  title: apiTopic.title,
  clusterId: apiTopic.cluster_id,
  primaryKeyword: apiTopic.primary_keyword,
  searchIntent: apiTopic.search_intent,
  funnel: apiTopic.funnel,
  format: apiTopic.format,
  angle: apiTopic.angle,
  priority: apiTopic.priority,
  status: apiTopic.status,
  sourceUrls: apiTopic.source_urls,
  postId: apiTopic.post_id,
  discardReason: apiTopic.discard_reason,
  evaluationScore: apiTopic.evaluation_score ?? null,
  evaluationVerdict: apiTopic.evaluation_verdict ?? null,
  evaluation: apiTopic.evaluation ? mapEvaluation(apiTopic.evaluation) : null,
  monthlySearches: apiTopic.monthly_searches ?? null,
  conflictPostId: apiTopic.conflict_post_id ?? null,
  evaluatedAt: apiTopic.evaluated_at ?? null,
  createdAt: apiTopic.created_at,
  updatedAt: apiTopic.updated_at,
});

const listTopics = async (params: ListTopicsParams = {}): Promise<BlogTopic[]> => {
  const { data } = await api.get<ApiTopic[]>(BACKOFFICE, {
    params: {
      cluster_id: params.clusterId || undefined,
      funnel: params.funnel || undefined,
      status: params.status || undefined,
    },
  });
  return (data ?? []).map(mapTopic);
};

const approveTopic = async (id: string): Promise<BlogTopic> => {
  const { data } = await api.patch<ApiTopic>(`${BACKOFFICE}/${id}/approve`);
  return mapTopic(data);
};

const discardTopic = async (id: string, reason: string): Promise<BlogTopic> => {
  const { data } = await api.patch<ApiTopic>(`${BACKOFFICE}/${id}/discard`, {
    reason,
  });
  return mapTopic(data);
};

const createDraft = async (id: string): Promise<TopicDraft> => {
  const { data } = await api.post<{ post_id: string; topic: ApiTopic }>(
    `${BACKOFFICE}/${id}/draft`
  );
  return { postId: data.post_id, topic: mapTopic(data.topic) };
};

export const httpTopicsService: TopicsService = {
  createDraft,
  listTopics,
  approveTopic,
  discardTopic,
};
